/**
 * ATHER - scroll reveal and limited depth.
 *
 * Contract with ather-motion.css:
 *   - `html.ather-motion` is the only thing that puts elements into a hidden
 *     state. It is added here, and only when motion is wanted. No JS, blocked
 *     JS, or `prefers-reduced-motion: reduce` therefore means fully visible
 *     content with no animation at all.
 *   - Nothing can stay invisible. Each element carries its own settle timer,
 *     so a transition that never runs is resolved for that element alone.
 *
 * There is no polling. The observer drives the reveals, one timer per revealed
 * element guards the transition, and the only extra passes are event-driven:
 * `load` and `pageshow`, which cover late layout and a bfcache restore.
 *
 * Theme editor: `shopify:section:load` re-scans the injected markup, and
 * elements are tracked in a WeakSet so re-entry cannot fire a reveal twice.
 */
(function () {
  'use strict';

  /**
   * What the observer watches. A masked heading is watched by its wrapper,
   * never by the inner line: the inner is translated 105% down inside a
   * clipping parent, so its intersection rectangle is permanently empty and
   * IntersectionObserver would never report it. The wrapper sits where the
   * heading sits, so it reports correctly, and revealing it reveals the line.
   */
  var OBSERVE_SELECTOR = '.ather-reveal, .ather-reveal-mask';

  /** Everything that carries a hidden state, wrappers included. */
  var REVEAL_SELECTOR = '.ather-reveal, .ather-reveal-mask, .ather-reveal-mask__inner';

  // How long a revealed element is given to finish its transition before it is
  // dropped into its final state. The slowest reveal is a 0.95s transform plus
  // a 0.48s stagger, so this leaves a comfortable margin.
  var SETTLE_AFTER_MS = 2200;

  var reduceQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  function prefersReducedMotion() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  var supported =
    'IntersectionObserver' in window &&
    'classList' in document.documentElement &&
    typeof document.documentElement.closest === 'function';

  var root = document.documentElement;
  var tracked = typeof WeakSet === 'function' ? new WeakSet() : null;
  var settleTimers = typeof Map === 'function' ? new Map() : null;
  var observer = null;
  var parallaxItems = [];
  var parallaxTicking = false;
  var parallaxBound = false;

  /* ---------------------------------------------------------------- reveal */

  function reveal(el) {
    if (el.classList.contains('is-revealed')) return;
    el.classList.add('is-revealed');

    // The wrapper carries the class so "already handled" checks stay uniform,
    // but the line inside it is what actually transitions.
    if (el.classList.contains('ather-reveal-mask')) {
      var inners = el.querySelectorAll('.ather-reveal-mask__inner');
      for (var i = 0; i < inners.length; i++) {
        inners[i].classList.add('is-revealed');
        guard(inners[i]);
      }
      return;
    }

    guard(el);
  }

  /** Drops the element out of the motion system entirely, at its final state. */
  function settle(el) {
    el.classList.add('is-revealed', 'is-settled');
    clearGuard(el);
  }

  function clearGuard(el) {
    if (!settleTimers) return;
    var timer = settleTimers.get(el);
    if (timer) window.clearTimeout(timer);
    settleTimers.delete(el);
  }

  /**
   * One timer per revealed element. If the transition finishes normally the
   * `transitionend` below cancels it; if the element is still fully
   * transparent when it fires - a throttled tab, a dropped frame, a browser
   * that never ran the transition - the element is settled outright.
   */
  function guard(el) {
    if (!settleTimers || settleTimers.has(el)) return;

    var timer = window.setTimeout(function () {
      settleTimers.delete(el);
      if (!el.isConnected) return;
      if (window.getComputedStyle(el).opacity === '0') settle(el);
    }, SETTLE_AFTER_MS);

    settleTimers.set(el, timer);
  }

  function revealAll(scope) {
    var nodes = (scope || document).querySelectorAll(REVEAL_SELECTOR);
    for (var i = 0; i < nodes.length; i++) reveal(nodes[i]);
  }

  function getObserver() {
    if (observer) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          reveal(entries[i].target);
          observer.unobserve(entries[i].target);
        }
      },
      {
        // Start a little before the element is fully on screen so the motion
        // resolves as it arrives rather than after it has landed.
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.01,
      }
    );

    return observer;
  }

  function revealLater(el) {
    return function () {
      window.requestAnimationFrame(function () {
        reveal(el);
      });
    };
  }

  function isInViewport(el) {
    var rect = el.getBoundingClientRect();
    return rect.top < (window.innerHeight || 0) && rect.bottom > 0;
  }

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll(OBSERVE_SELECTOR);
    var obs = getObserver();

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (tracked && tracked.has(el)) continue;
      if (tracked) tracked.add(el);

      // Anything already on screen at first paint - the hero, above-the-fold
      // copy - is revealed on the next frame instead of waiting for a scroll.
      // Content that opts in with data-ather-reveal-onload is revealed two
      // frames later, so its hidden state is painted first and the transition
      // (with its per-element delay) actually plays.
      if (isInViewport(el)) {
        if (el.closest('[data-ather-reveal-onload]') && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(revealLater(el));
        } else {
          reveal(el);
        }
        continue;
      }

      applyStagger(el);
      obs.observe(el);
    }
  }

  /**
   * Siblings inside a `[data-ather-stagger]` container step in one after the
   * other. Capped so a long grid never makes the last card feel late.
   */
  function applyStagger(el) {
    var group = el.closest('[data-ather-stagger]');
    if (!group || el.style.getPropertyValue('--ather-reveal-delay')) return;

    var step = parseInt(group.getAttribute('data-ather-stagger'), 10);
    if (!step || step < 0) step = 90;

    var siblings = group.querySelectorAll(OBSERVE_SELECTOR);
    var index = Array.prototype.indexOf.call(siblings, el);
    if (index < 0) return;

    var delay = Math.min(index * step, 480);
    el.style.setProperty('--ather-reveal-delay', delay + 'ms');
  }

  /* -------------------------------------------------------------- parallax */

  function collectParallax(scope) {
    var nodes = (scope || document).querySelectorAll('[data-ather-parallax]');

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var inner = el.querySelector('.ather-parallax__inner');
      if (!inner) continue;

      var already = false;
      for (var j = 0; j < parallaxItems.length; j++) {
        if (parallaxItems[j].el === el) {
          already = true;
          break;
        }
      }
      if (already) continue;

      var depth = parseFloat(el.getAttribute('data-ather-parallax'));
      if (!depth || depth <= 0) depth = 0.12;
      // Hard cap: a large shift breaks the frame of a product photograph.
      depth = Math.min(depth, 0.25);

      parallaxItems.push({ el: el, inner: inner, depth: depth });
    }

    if (parallaxItems.length && !parallaxBound) {
      parallaxBound = true;
      window.addEventListener('scroll', requestParallax, { passive: true });
      window.addEventListener('resize', requestParallax, { passive: true });
    }

    requestParallax();
  }

  function requestParallax() {
    if (parallaxTicking || !parallaxItems.length) return;
    parallaxTicking = true;
    window.requestAnimationFrame(updateParallax);
  }

  function updateParallax() {
    parallaxTicking = false;
    if (window.innerWidth < 990) return;

    var vh = window.innerHeight || 0;

    for (var i = 0; i < parallaxItems.length; i++) {
      var item = parallaxItems[i];
      if (!item.el.isConnected) continue;

      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) continue;

      // -1 when the element is just below the fold, +1 when just above it.
      var progress = (vh / 2 - (rect.top + rect.height / 2)) / (vh / 2 + rect.height / 2);
      var shift = progress * item.depth * 100;

      item.inner.style.transform = 'translate3d(0, ' + shift.toFixed(2) + 'px, 0)';
    }
  }

  function clearParallax() {
    for (var i = 0; i < parallaxItems.length; i++) {
      parallaxItems[i].inner.style.transform = '';
    }
    parallaxItems = [];
  }

  /* ------------------------------------------------------------------ init */

  /**
   * A single pass over elements that are on screen but still unrevealed.
   * Event-driven only: `load`, when late layout may have moved something into
   * view before the observer was watching it, and `pageshow`, for a restore
   * from the back/forward cache.
   */
  function catchUp() {
    var nodes = document.querySelectorAll(OBSERVE_SELECTOR);

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.classList.contains('is-revealed')) continue;
      if (isInViewport(el)) reveal(el);
    }
  }

  function enable(scope) {
    root.classList.add('ather-motion');
    try {
      scan(scope);
      collectParallax(scope);
    } catch (error) {
      // Arming the hidden state and then failing would hide the shop. Back out.
      disable();
      throw error;
    }
  }

  function disable() {
    if (settleTimers) {
      settleTimers.forEach(function (timer) {
        window.clearTimeout(timer);
      });
      settleTimers.clear();
    }
    root.classList.remove('ather-motion');
    revealAll(document);
    clearParallax();
  }

  function init() {
    if (!supported || prefersReducedMotion()) {
      // Nothing is hidden in this branch; the CSS hidden state is never armed.
      return;
    }

    enable(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // A finished transition needs no guard; cancelling it keeps the timer map
  // from holding references to elements that are already done.
  document.addEventListener(
    'transitionend',
    function (event) {
      if (event.propertyName !== 'opacity') return;
      var el = event.target;
      if (el instanceof Element && el.classList.contains('is-revealed')) clearGuard(el);
    },
    true
  );

  window.addEventListener('load', function () {
    if (supported && !prefersReducedMotion()) catchUp();
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted && supported && !prefersReducedMotion()) catchUp();
  });

  // Respond live when the visitor flips the OS setting.
  if (reduceQuery) {
    var onChange = function () {
      if (prefersReducedMotion()) {
        disable();
      } else if (supported) {
        enable(document);
      }
    };
    if (typeof reduceQuery.addEventListener === 'function') {
      reduceQuery.addEventListener('change', onChange);
    } else if (typeof reduceQuery.addListener === 'function') {
      reduceQuery.addListener(onChange);
    }
  }

  /* --------------------------------------------------------- theme editor */

  document.addEventListener('shopify:section:load', function (event) {
    if (!supported || prefersReducedMotion()) return;
    var scope = event.target instanceof Element ? event.target : document;
    enable(scope);
    // The section is injected before it is laid out, so a first pass can read
    // a zero-height rect. Re-scan once the browser has placed it; `scan`
    // reveals whatever is already on screen, which is the section the merchant
    // is editing, and `tracked` keeps the second pass from firing it twice.
    window.setTimeout(function () {
      scan(scope);
    }, 50);
  });

  document.addEventListener('shopify:section:unload', function () {
    parallaxItems = parallaxItems.filter(function (item) {
      return item.el.isConnected;
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    if (event.target instanceof Element) revealAll(event.target);
  });
})();
